/* eslint-disable */
import { inject, Injectable } from '@angular/core';
import { CredentialsDTO } from '../../application/models';
import { AuthProviderId } from '../models';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithPopup,
  signInWithEmailAndPassword,
  signInWithPopup,
  updatePassword,
  User
} from '@angular/fire/auth';
import { HttpClient } from '@angular/common/http';
import {
  AppsFlyerService,
  AUTH_CLIENT_TOKEN,
  AuthClientProvider,
  ENV_CONFIG,
  EnvConfig,
  PLATFORM_TOKEN,
  PlatformProvider
} from '@core';
import { firstValueFrom, from, Observable, switchMap, timeout } from 'rxjs';
import { FirebaseAuthentication, GetCurrentUserResult } from '@capacitor-firebase/authentication';

type NativeAuthProvider = 'google' | 'apple' | 'facebook';

const NATIVE_AUTH_FNS: Record<NativeAuthProvider, () => Promise<unknown>> = {
  google: () => FirebaseAuthentication.signInWithGoogle(),
  apple: () => FirebaseAuthentication.signInWithApple(),
  facebook: () => FirebaseAuthentication.signInWithFacebook()
};

@Injectable()
export class AuthService {
  private readonly authClientProvider: AuthClientProvider = inject(AUTH_CLIENT_TOKEN);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly appsFlyerService: AppsFlyerService = inject(AppsFlyerService);

  private isNative: boolean = this.platformProvider.isNativeRun;

  async login(credentials: CredentialsDTO): Promise<void> {
    if (this.isNative) {
      await FirebaseAuthentication.signInWithEmailAndPassword({
        email: credentials.email,
        password: credentials.password
      });
      return;
    }

    await signInWithEmailAndPassword(this.authClientProvider.authClient, credentials.email, credentials.password);
  }

  async register(credentials: CredentialsDTO): Promise<void> {
    this.appsFlyerService.logEvent('fb_register');

    if (this.isNative) {
      await FirebaseAuthentication.createUserWithEmailAndPassword({
        email: credentials.email,
        password: credentials.password
      });
      return;
    }

    await createUserWithEmailAndPassword(this.authClientProvider.authClient, credentials.email, credentials.password);
  }

  async authWithGoogle(): Promise<void> {
    if (this.isNative) return this.runNativeAuth('google');

    await signInWithPopup(this.authClientProvider.authClient, new OAuthProvider('google.com'));
  }

  async authWithApple(): Promise<void> {
    if (this.isNative) return this.runNativeAuth('apple');

    await signInWithPopup(this.authClientProvider.authClient, new OAuthProvider('apple.com'));
  }

  async authWithFacebook(): Promise<void> {
    if (this.isNative) return this.runNativeAuth('facebook');

    await signInWithPopup(this.authClientProvider.authClient, new OAuthProvider('facebook.com'));
  }

  async logout(): Promise<void> {
    if (this.isNative) {
      await FirebaseAuthentication.signOut();
    } else {
      await this.authClientProvider.authClient.signOut();
    }

    localStorage.clear();
  }

  removeAccount(): Observable<void> {
    return this.httpClient.delete<void>(`${this.env.apiUrl}/users/me`).pipe(
      switchMap(() => {
        if (this.isNative) {
          return from(FirebaseAuthentication.deleteUser());
        }

        const user = this.authClientProvider.authClient.currentUser;
        if (user) {
          return from(deleteUser(user));
        }
        return from(Promise.resolve());
      })
    );
  }

  async getCurrentProviderId(): Promise<AuthProviderId> {
    const result: GetCurrentUserResult = await FirebaseAuthentication.getCurrentUser();
    const providerData = result.user?.providerData ?? [];
    const providerIds: Set<string> = new Set(providerData.map((p) => p.providerId));

    const priority: AuthProviderId[] = [
      AuthProviderId.Google,
      AuthProviderId.Apple,
      AuthProviderId.Facebook,
      AuthProviderId.Password
    ];

    const match: AuthProviderId | undefined = priority.find((p: AuthProviderId) => providerIds.has(p));
    if (match) return match;

    const ids: string = providerData.map((p) => p.providerId).join(', ') || 'none';
    throw new Error(`Unsupported auth provider: ${ids}`);
  }

  reauthenticate(password?: string): Observable<void> {
    return from(this.reauthenticateAsync(password));
  }

  async updateUserPassword(newPassword: string): Promise<void> {
    if (this.isNative) {
      await FirebaseAuthentication.updatePassword({ newPassword });
      return;
    }

    const user: User | null = this.authClientProvider.authClient.currentUser;
    if (!user) throw new Error('No user is currently signed in.');

    await updatePassword(user, newPassword);
  }

  private async reauthenticateAsync(password?: string): Promise<void> {
    const providerId: AuthProviderId = await this.getCurrentProviderId();

    switch (providerId) {
      case AuthProviderId.Password:
        if (!password) throw new Error('Password is required to reauthenticate.');
        return this.reauthWithPassword(password);
      case AuthProviderId.Google:
        return this.reauthWithGoogle();
      case AuthProviderId.Apple:
        return this.reauthWithApple();
      case AuthProviderId.Facebook:
        return this.reauthWithFacebook();
    }
  }

  private async reauthWithPassword(password: string): Promise<void> {
    const result = await FirebaseAuthentication.getCurrentUser();
    if (!result.user?.email) throw new Error('No user is currently signed in.');

    if (this.isNative) {
      await FirebaseAuthentication.signInWithEmailAndPassword({
        email: result.user.email,
        password
      });
      return;
    }

    await signInWithEmailAndPassword(this.authClientProvider.authClient, result.user.email, password);
  }

  private async reauthWithGoogle(): Promise<void> {
    if (this.isNative) return this.runNativeAuth('google');

    const user: User | null = this.authClientProvider.authClient.currentUser;
    if (!user) throw new Error('No user is currently signed in.');
    await reauthenticateWithPopup(user, new GoogleAuthProvider());
  }

  private async reauthWithApple(): Promise<void> {
    if (this.isNative) return this.runNativeAuth('apple');

    const user = this.authClientProvider.authClient.currentUser;
    if (!user) throw new Error('No user is currently signed in.');
    await reauthenticateWithPopup(user, new OAuthProvider('apple.com'));
  }

  private async reauthWithFacebook(): Promise<void> {
    if (this.isNative) return this.runNativeAuth('facebook');

    const user = this.authClientProvider.authClient.currentUser;
    if (!user) throw new Error('No user is currently signed in.');
    await reauthenticateWithPopup(user, new OAuthProvider('facebook.com'));
  }

  private async runNativeAuth(provider: NativeAuthProvider): Promise<void> {
    const NATIVE_AUTH_TIMEOUT_MS: number = 10_000;

    await firstValueFrom(from(NATIVE_AUTH_FNS[provider]()).pipe(timeout(NATIVE_AUTH_TIMEOUT_MS)));
  }
}
